import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Stack, Alert,
  MenuItem, FormControlLabel, Switch, Autocomplete, Chip, Typography, IconButton,
  Box, CircularProgress, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  SalonMember, TaskCategory, TaskDetail, TaskPriority, taskApi, taskErrorMessage,
} from '../../services/taskService';
import { tokens } from '../../theme';
import { FieldGrid, PersonAvatar, useConfirm } from '../../components/ui';
import { PRIORITY_COLOR, PRIORITY_LABEL, PRIORITY_ORDER, userLabel } from './taskShared';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  salonId: string;
  task?: TaskDetail | null;
  members: SalonMember[];
  categories: TaskCategory[];
  /** Owners may assign anyone; employees can only create work for themselves. */
  isManager: boolean;
  currentUserId: string;
}

interface FormState {
  title: string;
  description: string;
  priority: TaskPriority;
  categoryId: string;
  assigneeIds: string[];
  startDate: string;
  startTime: string;
  dueDate: string;
  dueTime: string;
  estimatedMinutes: string;
  requiresApproval: boolean;
}

type FieldKey = 'title' | 'dueDate' | 'estimatedMinutes';

const EMPTY: FormState = {
  title: '',
  description: '',
  priority: 'NORMAL',
  categoryId: '',
  assigneeIds: [],
  startDate: '',
  startTime: '',
  dueDate: '',
  dueTime: '',
  estimatedMinutes: '',
  requiresApproval: false,
};

/** Splits an ISO instant into the date and time inputs the form uses. */
function splitDate(iso?: string | null, hasTime = false): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: hasTime ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : '',
  };
}

/** Builds a local-time instant from the two inputs (no timezone surprises). */
function joinDate(date: string, time: string): { iso: string | null; hasTime: boolean } {
  if (!date) return { iso: null, hasTime: false };
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return { iso: null, hasTime: false };
  if (time) {
    const [hh, mm] = time.split(':').map(Number);
    return { iso: new Date(y, m - 1, d, hh || 0, mm || 0).toISOString(), hasTime: true };
  }
  // No time given → end of day, so "termin: 12.05" means "do końca 12 maja".
  return { iso: new Date(y, m - 1, d, 23, 59, 0).toISOString(), hasTime: false };
}

/** Sekcja okna: tytul z krotkim opisem nad polami, oddzielona linia od poprzedniej. */
const DialogSection: React.FC<{ title: string; description?: string; first?: boolean; children: React.ReactNode }> = ({
  title, description, first, children,
}) => (
  <Box component="section" sx={{ pt: first ? 0.5 : 2.5, mt: first ? 0 : 2.5, borderTop: first ? 'none' : `1px solid ${tokens.line}` }}>
    <Typography component="h3" sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.4 }}>{title}</Typography>
    {description && (
      <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, mt: 0.25 }}>{description}</Typography>
    )}
    <Box sx={{ mt: 2 }}>{children}</Box>
  </Box>
);

const Dot: React.FC<{ color: string }> = ({ color }) => (
  <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, display: 'inline-block', mr: 1.25, flexShrink: 0 }} />
);

export const TaskFormDialog: React.FC<Props> = ({
  open, onClose, onSaved, salonId, task, members, categories, isManager, currentUserId,
}) => {
  const isEdit = !!task;
  const confirm = useConfirm();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [initial, setInitial] = useState<FormState>(EMPTY);
  const [checklist, setChecklist] = useState<string[]>([]);
  const [checklistDraft, setChecklistDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState<{ field: FieldKey; message: string } | null>(null);

  useEffect(() => {
    if (!open) return;
    const next: FormState = task
      ? {
          title: task.title,
          description: task.description || '',
          priority: task.priority,
          categoryId: task.category?.id || '',
          assigneeIds: task.assignees.map((a) => a.id),
          ...(() => {
            const s = splitDate(task.startAt, task.startHasTime);
            const d = splitDate(task.dueAt, task.dueHasTime);
            return { startDate: s.date, startTime: s.time, dueDate: d.date, dueTime: d.time };
          })(),
          estimatedMinutes: task.estimatedMinutes ? String(task.estimatedMinutes) : '',
          requiresApproval: task.requiresApproval,
        }
      : { ...EMPTY, assigneeIds: isManager ? [] : [currentUserId] };
    setForm(next);
    setInitial(next);
    setChecklist([]);
    setChecklistDraft('');
    setError('');
    setFieldError(null);
  }, [open, task, isManager, currentUserId]);

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initial) || checklist.length > 0,
    [form, initial, checklist],
  );

  // Protects against losing a half-written task on a browser/tab close.
  useEffect(() => {
    if (!open || !dirty) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [open, dirty]);

  const requestClose = useCallback(async () => {
    if (dirty && !saving) {
      const discard = await confirm({
        title: 'Odrzucić wprowadzone dane?',
        description: 'Masz niezapisane zmiany. Jeśli zamkniesz okno, zostaną utracone.',
        confirmLabel: 'Odrzuć zmiany',
        cancelLabel: 'Wróć do edycji',
        tone: 'danger',
      });
      if (discard) onClose();
      return;
    }
    onClose();
  }, [dirty, saving, onClose, confirm]);

  const assignableMembers = isManager
    ? members
    : members.filter((m) => m.userId === currentUserId);

  const selectedMembers = members.filter((m) => form.assigneeIds.includes(m.userId));

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (fieldError && (fieldError.field === key || (fieldError.field === 'dueDate' && key === 'startDate'))) setFieldError(null);
  };

  const addChecklistLine = () => {
    const value = checklistDraft.trim();
    if (!value) return;
    setChecklist((prev) => [...prev, value]);
    setChecklistDraft('');
  };

  const handleSave = async () => {
    const title = form.title.trim();
    if (title.length < 3) {
      setFieldError({ field: 'title', message: 'Tytuł musi mieć co najmniej 3 znaki.' });
      return;
    }
    const start = joinDate(form.startDate, form.startTime);
    const due = joinDate(form.dueDate, form.dueTime);
    if (start.iso && due.iso && new Date(due.iso) < new Date(start.iso)) {
      setFieldError({ field: 'dueDate', message: 'Termin wykonania nie może być wcześniejszy niż data rozpoczęcia.' });
      return;
    }
    const estimated = form.estimatedMinutes ? parseInt(form.estimatedMinutes, 10) : null;
    if (estimated !== null && (Number.isNaN(estimated) || estimated < 1)) {
      setFieldError({ field: 'estimatedMinutes', message: 'Podaj liczbę minut większą od zera albo zostaw pole puste.' });
      return;
    }

    setSaving(true);
    setError('');
    setFieldError(null);
    try {
      const payload: Record<string, unknown> = {
        title,
        description: form.description.trim() || null,
        priority: form.priority,
        categoryId: form.categoryId || null,
        assigneeIds: form.assigneeIds,
        startAt: start.iso,
        startHasTime: start.hasTime,
        dueAt: due.iso,
        dueHasTime: due.hasTime,
        estimatedMinutes: estimated,
        requiresApproval: form.requiresApproval,
      };
      if (isEdit && task) {
        await taskApi.update(task.id, payload);
      } else {
        await taskApi.create({ ...payload, salonId, checklist });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(taskErrorMessage(err, 'Nie udało się zapisać zadania. Sprawdź dane i spróbuj ponownie.'));
    } finally {
      setSaving(false);
    }
  };

  const errorFor = (field: FieldKey) => (fieldError?.field === field ? fieldError.message : undefined);

  return (
    <Dialog open={open} onClose={requestClose} maxWidth="sm" fullWidth aria-labelledby="task-form-title">
      <DialogTitle id="task-form-title" sx={{ pr: 7 }}>
        {isEdit ? 'Edytuj zadanie' : 'Nowe zadanie'}
        <IconButton aria-label="Zamknij" onClick={requestClose} sx={{ position: 'absolute', right: 14, top: 16 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}

        <DialogSection title="Co trzeba zrobić" first>
          <Stack gap={2}>
            <TextField
              label="Tytuł zadania"
              required
              fullWidth
              autoFocus
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
              placeholder="np. Zamówienie kosmetyków na przyszły tydzień"
              inputProps={{ maxLength: 180 }}
              error={!!errorFor('title')}
              helperText={errorFor('title') || 'Krótko i konkretnie — tytuł widać na tablicy zadań.'}
            />
            <TextField
              label="Szczegółowy opis"
              fullWidth
              multiline
              minRows={3}
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              placeholder="Co dokładnie trzeba zrobić? Ważne szczegóły, linki, ustalenia."
            />
            <FieldGrid columns={2}>
              <TextField
                select label="Priorytet" fullWidth value={form.priority}
                onChange={(e) => update('priority', e.target.value as TaskPriority)}
              >
                {PRIORITY_ORDER.map((p) => (
                  <MenuItem key={p} value={p}>
                    <Dot color={PRIORITY_COLOR[p]} />
                    {PRIORITY_LABEL[p]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select label="Kategoria" fullWidth value={form.categoryId}
                onChange={(e) => update('categoryId', e.target.value)}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">Bez kategorii</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    <Dot color={c.color} />
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </FieldGrid>
          </Stack>
        </DialogSection>

        <DialogSection
          title="Kto się tym zajmie"
          description={isManager ? 'Możesz przypisać jedną lub kilka osób — także siebie.' : 'Możesz tworzyć zadania tylko dla siebie.'}
        >
          <Autocomplete
            multiple
            options={assignableMembers}
            getOptionLabel={(m) => userLabel({ id: m.userId, ...m })}
            isOptionEqualToValue={(a, b) => a.userId === b.userId}
            value={selectedMembers}
            onChange={(_, value) => update('assigneeIds', value.map((v) => v.userId))}
            disabled={!isManager && assignableMembers.length <= 1}
            noOptionsText="Brak pracowników do wyboru"
            renderOption={(props, m) => (
              <li {...props} key={m.userId}>
                <PersonAvatar firstName={m.firstName} lastName={m.lastName} fallback={m.email} seed={m.userId} size={28} sx={{ mr: 1.25, fontSize: '0.75rem' }} />
                {userLabel({ id: m.userId, ...m })}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((m, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={m.userId}
                  size="small"
                  label={userLabel({ id: m.userId, ...m })}
                  avatar={<PersonAvatar firstName={m.firstName} lastName={m.lastName} fallback={m.email} seed={m.userId} size={18} sx={{ ml: '3px !important', mr: '-4px !important', fontSize: '0.75rem !important' }} />}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Przypisane osoby"
                placeholder={form.assigneeIds.length ? '' : 'Wybierz osoby'}
              />
            )}
          />
        </DialogSection>

        <DialogSection title="Terminy" description="Bez godziny termin obowiązuje do końca wybranego dnia.">
          <FieldGrid columns={2}>
            <TextField
              label="Data rozpoczęcia" type="date" fullWidth
              value={form.startDate}
              onChange={(e) => update('startDate', e.target.value)}
            />
            <TextField
              label="Godzina rozpoczęcia" type="time" fullWidth
              value={form.startTime}
              disabled={!form.startDate}
              onChange={(e) => update('startTime', e.target.value)}
              helperText="Opcjonalnie"
            />
            <TextField
              label="Termin wykonania" type="date" fullWidth
              value={form.dueDate}
              onChange={(e) => update('dueDate', e.target.value)}
              error={!!errorFor('dueDate')}
              helperText={errorFor('dueDate')}
            />
            <TextField
              label="Godzina terminu" type="time" fullWidth
              value={form.dueTime}
              disabled={!form.dueDate}
              onChange={(e) => update('dueTime', e.target.value)}
              helperText="Opcjonalnie"
            />
            <TextField
              label="Szacowany czas realizacji" type="number" fullWidth
              value={form.estimatedMinutes}
              onChange={(e) => update('estimatedMinutes', e.target.value)}
              inputProps={{ min: 1, max: 43200, inputMode: 'numeric' }}
              InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
              error={!!errorFor('estimatedMinutes')}
              helperText={errorFor('estimatedMinutes') || 'Pomaga porównać plan z rzeczywistym czasem.'}
            />
          </FieldGrid>
        </DialogSection>

        <DialogSection title="Zamknięcie zadania">
          <FormControlLabel
            control={(
              <Switch
                checked={form.requiresApproval}
                onChange={(e) => update('requiresApproval', e.target.checked)}
              />
            )}
            label={(
              <Box>
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>Wymaga akceptacji właściciela</Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted }}>
                  Po wykonaniu zadanie trafi do zakładki „Do akceptacji”, zamiast od razu się zamknąć.
                </Typography>
              </Box>
            )}
            sx={{ alignItems: 'flex-start', '& .MuiSwitch-root': { mt: 0.25, flexShrink: 0 } }}
          />
        </DialogSection>

        {!isEdit && (
          <DialogSection title="Checklista" description="Rozpisz zadanie na kroki — każdy da się odhaczyć osobno.">
            <Stack direction="row" gap={1} alignItems="flex-start">
              <TextField
                placeholder="np. Sprawdzić stan zapasów"
                fullWidth
                value={checklistDraft}
                onChange={(e) => setChecklistDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addChecklistLine();
                  }
                }}
                inputProps={{ maxLength: 180, 'aria-label': 'Nowa pozycja checklisty' }}
                helperText="Wpisz krok i naciśnij Enter."
              />
              <Button variant="outlined" onClick={addChecklistLine} startIcon={<AddIcon />} disabled={!checklistDraft.trim()} sx={{ flexShrink: 0, mt: '1px' }}>
                Dodaj
              </Button>
            </Stack>
            {checklist.length > 0 && (
              <Stack component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 1.5, border: `1px solid ${tokens.line}`, borderRadius: '12px' }}>
                {checklist.map((item, idx) => (
                  <Stack
                    component="li"
                    key={`${item}-${idx}`}
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{ pl: 1.5, pr: 0.75, py: 0.5, borderTop: idx === 0 ? 'none' : `1px solid ${tokens.line}` }}
                  >
                    <CheckBoxOutlineBlankIcon sx={{ fontSize: 18, color: tokens.subtle }} />
                    <Typography sx={{ fontSize: '0.9375rem', flexGrow: 1, minWidth: 0, wordBreak: 'break-word' }}>{item}</Typography>
                    <IconButton
                      size="small"
                      aria-label={`Usuń pozycję ${item}`}
                      onClick={() => setChecklist((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}
          </DialogSection>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={requestClose} disabled={saving}>Anuluj</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || form.title.trim().length < 3}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isEdit ? 'Zapisz zmiany' : 'Utwórz zadanie'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskFormDialog;
